// ============================================================================
// budgetExpenseFormSections — Shared Form Schema for Budget Expenses
// ----------------------------------------------------------------------------
// Used by EntityFormDialog in both NodeDetailDrawer and BudgetHubLayout.
// ============================================================================

import { BUDGET_CATEGORIES } from '../../types/budget'
import type { FieldSection } from '../Common/EntityFormDialog'

export const getBudgetExpenseFormSections = (currencyCode: string = 'USD'): FieldSection[] => [
  {
    id: 'basics',
    label: 'Basics',
    fields: [
      {
        id: 'amount',
        label: 'Amount',
        type: 'currency',
        required: true,
        prefix: currencyCode,
        minorUnits: true,
        placeholder: '0.00',
        validate: (val) => {
          const num = Number(val)
          if (isNaN(num) || num <= 0) return 'Amount must be greater than zero'
          return null
        },
      },
      {
        id: 'currency',
        label: 'Currency',
        type: 'text',
        disabled: true,
        defaultValue: currencyCode,
      },
      {
        id: 'description',
        label: 'Description',
        type: 'text',
        required: true,
        placeholder: 'What was this expense for?',
        validate: (val) => {
          const str = String(val ?? '')
          if (!str.trim()) return 'Description is required'
          if (str.length > 200) return 'Description must be 200 characters or less'
          return null
        },
      },
      {
        id: 'category',
        label: 'Category',
        type: 'select',
        required: true,
        defaultValue: BUDGET_CATEGORIES[0],
        options: BUDGET_CATEGORIES.map((cat) => ({ label: cat, value: cat })),
        creatable: true,
        placeholder: 'Select or type category...',
      },
      {
        id: 'tags',
        label: 'Tags',
        type: 'tagInput',
        placeholder: 'Add tags (press Enter)...',
      },
      {
        id: 'isIncome',
        label: 'Is Income',
        type: 'switch',
        defaultValue: false,
        hint: 'Toggle on if this entry represents received income rather than an expense.',
      },
    ],
  },
  {
    id: 'linked_entity',
    label: 'Linked Entity',
    hint: 'Attach this expense to a Goal, Task, or Checklist.',
    fields: [
      {
        id: 'linkedEntity',
        label: 'Target item',
        type: 'entityPicker',
        required: true,
        defaultValue: null,
      },
    ],
  },
  {
    id: 'vendor_payment',
    label: 'Vendor & Payment',
    fields: [
      {
        id: 'vendorId',
        label: 'Vendor',
        type: 'vendorPicker',
        placeholder: 'Select or add vendor...',
      },
      {
        id: 'paymentMethod',
        label: 'Payment method',
        type: 'select',
        defaultValue: 'card',
        options: [
          { label: 'Card', value: 'card' },
          { label: 'Cash', value: 'cash' },
          { label: 'Bank Transfer', value: 'bank' },
          { label: 'UPI / Digital', value: 'upi' },
          { label: 'Other', value: 'other' },
        ],
      },
    ],
  },
  {
    id: 'tax',
    label: 'Tax details',
    fields: [
      {
        id: 'taxAmount',
        label: 'Tax amount',
        type: 'currency',
        prefix: currencyCode,
        minorUnits: true,
        placeholder: '0.00',
      },
      {
        id: 'taxInclusive',
        label: 'Tax inclusive',
        type: 'switch',
        defaultValue: true,
        hint: 'When enabled, the total amount includes the tax amount.',
      },
    ],
  },
  {
    id: 'receipts',
    label: 'Receipts & Attachments',
    fields: [
      {
        id: 'receipt',
        label: 'Primary receipt',
        type: 'image',
        hint: 'Upload receipt photo or PDF',
      },
      {
        id: 'attachments',
        label: 'Additional attachments',
        type: 'multiselect', // or specialized file list/dropzone
        placeholder: 'Upload extra receipts or documents...',
      },
    ],
  },
  {
    id: 'recurring',
    label: 'Recurring Schedule',
    fields: [
      {
        id: 'isRecurring',
        label: 'Make recurring',
        type: 'switch',
        defaultValue: false,
      },
      {
        id: 'recurringCadence',
        label: 'Cadence',
        type: 'select',
        defaultValue: 'monthly',
        options: [
          { label: 'Weekly', value: 'weekly' },
          { label: 'Monthly', value: 'monthly' },
          { label: 'Quarterly', value: 'quarterly' },
          { label: 'Yearly', value: 'yearly' },
        ],
      },
      {
        id: 'recurringDayOfPeriod',
        label: 'Day of period',
        type: 'number',
        min: 1,
        max: 31,
        defaultValue: 1,
        hint: 'Day of week (1-7) for weekly, or day of month (1-31) for monthly/quarterly/yearly.',
      },
    ],
  },
]
